import { type Profile } from '#types/database.types'

export interface ClusterMarkerProps {
	profiles: Profile[]
	pointCount: number
	onClusterClick: () => void
}

// Calculate position in circular radial pattern
function getRadialPosition(
	index: number,
	totalCount: number,
): { x: number; y: number } {
	if (index === 0) {
		// Center position
		return { x: 0, y: 0 }
	}

	// Calculate which ring and position within that ring
	let ring = 1
	let positionsInRing = 6 // First ring has 6 positions
	let positionsFilled = 1 // Center is filled

	// Find which ring this index belongs to
	while (positionsFilled + positionsInRing < index + 1) {
		positionsFilled += positionsInRing
		ring++
		positionsInRing = ring * 6 // Each ring has 6 * ring positions
	}

	// Position within current ring
	const positionInRing = index - positionsFilled
	const angle = (positionInRing / positionsInRing) * Math.PI * 2

	// Distance from center (increases with each ring)
	const radius = ring * 24 // 24px apart per ring

	return {
		x: Math.cos(angle) * radius,
		y: Math.sin(angle) * radius,
	}
}

export function ClusterMarker({
	profiles,
	pointCount,
	onClusterClick,
}: ClusterMarkerProps) {
	const avatarSize = 32

	return (
		<div
			className="mapbox-cluster-marker"
			style={{
				position: 'relative',
				width: 0,
				height: 0,
			}}
		>
			{profiles.map((profile, index) => {
				const { x, y } = getRadialPosition(index, profiles.length)

				return (
					<div
						key={`${profile.x_handle}-${index}`}
						style={{
							position: 'absolute',
							left: `${x}px`,
							top: `${y}px`,
							transform: 'translate(-50%, -50%)',
							cursor: 'pointer',
							transition: 'transform 0.3s ease',
							width: `${avatarSize}px`,
							height: `${avatarSize}px`,
						}}
						onClick={onClusterClick}
						onMouseEnter={(e) => {
							e.currentTarget.style.transform =
								'translate(-50%, -50%) scale(1.2)'
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'
						}}
					>
						<div
							style={{
								width: '100%',
								height: '100%',
								borderRadius: '50%',
								overflow: 'hidden',
								border: '2px solid rgba(99, 102, 241, 0.8)',
								boxShadow: '0 0 10px rgba(99, 102, 241, 0.5)',
								background: '#1a1a1a',
							}}
						>
							<img
								src={profile.avatar_url || ''}
								alt={profile.x_handle}
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'cover',
								}}
								onError={(e) => {
									const target = e.target as HTMLImageElement
									target.src =
										'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22%3E%3Crect width=%2232%22 height=%2232%22 fill=%22%236366f1%22/%3E%3C/svg%3E'
								}}
							/>
						</div>
					</div>
				)
			})}
		</div>
	)
}
